import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import DashboardHeader from '../../components/common/DashboardHeader';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  TruckIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ReportData {
  total_revenue: number;
  monthly_revenue: number;
  total_garages: number;
  active_garages: number;
  total_customers: number;
  total_job_cards: number;
  revenue_trend: Array<{ month: string; revenue: number }>;
  garage_growth: Array<{ month: string; count: number }>;
}

const ReportsPage: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [exportFormat, setExportFormat] = useState('pdf');

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/reports', {
        params: { range: dateRange },
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (type: string) => {
    try {
      const response = await api.post('/admin/reports/export', {
        type,
        format: exportFormat,
        range: dateRange,
      });
      toast.success(`${type} report exported successfully`);
      // Handle file download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.${exportFormat}`;
      a.click();
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
    }).format(amount);
  };

  const reportTypes = [
    {
      id: 'revenue',
      title: 'Revenue Report',
      description: 'Detailed breakdown of platform revenue',
      icon: CurrencyDollarIcon,
      color: 'bg-green-500',
    },
    {
      id: 'garages',
      title: 'Garage Performance',
      description: 'Performance metrics for all garages',
      icon: BuildingStorefrontIcon,
      color: 'bg-blue-500',
    },
    {
      id: 'customers',
      title: 'Customer Analytics',
      description: 'Customer acquisition and retention',
      icon: UsersIcon,
      color: 'bg-purple-500',
    },
    {
      id: 'operations',
      title: 'Operations Report',
      description: 'Job cards and service analytics',
      icon: ClipboardDocumentListIcon,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
                Reports & Analytics
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Generate and export detailed reports</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="last_year">Last Year</option>
              </select>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="csv">CSV</option>
              </select>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <CurrencyDollarIcon className="w-6 sm:w-8 h-6 sm:h-8 text-green-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total Revenue</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {reportData ? formatCurrency(reportData.total_revenue) : '-'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <BuildingStorefrontIcon className="w-6 sm:w-8 h-6 sm:h-8 text-blue-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Active Garages</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {reportData ? reportData.active_garages : '-'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <UsersIcon className="w-6 sm:w-8 h-6 sm:h-8 text-purple-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total Customers</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {reportData ? reportData.total_customers.toLocaleString() : '-'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <ClipboardDocumentListIcon className="w-6 sm:w-8 h-6 sm:h-8 text-orange-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Job Cards</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {reportData ? reportData.total_job_cards.toLocaleString() : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Report Types */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Available Reports</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {reportTypes.map((report) => (
              <div
                key={report.id}
                className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className={`${report.color} p-2 sm:p-3 rounded-lg`}>
                      <report.icon className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900">{report.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">{report.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => exportReport(report.id)}
                    className="bg-[#008B8B] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-[#006666] flex items-center text-sm"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Export</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Revenue Trend */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B]"></div>
              </div>
            ) : (
              <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 text-sm">Chart visualization would go here</p>
              </div>
            )}
          </div>

          {/* Garage Growth */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Garage Growth</h3>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B]"></div>
              </div>
            ) : (
              <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500 text-sm">Chart visualization would go here</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mt-6 sm:mt-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button className="bg-gray-100 hover:bg-gray-200 p-3 sm:p-4 rounded-lg text-left transition-colors">
              <CalendarIcon className="w-5 h-5 text-gray-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Schedule Report</p>
              <p className="text-xs text-gray-500">Automate report generation</p>
            </button>
            <button className="bg-gray-100 hover:bg-gray-200 p-3 sm:p-4 rounded-lg text-left transition-colors">
              <DocumentArrowDownIcon className="w-5 h-5 text-gray-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Bulk Export</p>
              <p className="text-xs text-gray-500">Export multiple reports</p>
            </button>
            <button className="bg-gray-100 hover:bg-gray-200 p-3 sm:p-4 rounded-lg text-left transition-colors">
              <ChartBarIcon className="w-5 h-5 text-gray-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Custom Report</p>
              <p className="text-xs text-gray-500">Create custom analytics</p>
            </button>
            <button className="bg-gray-100 hover:bg-gray-200 p-3 sm:p-4 rounded-lg text-left transition-colors">
              <TruckIcon className="w-5 h-5 text-gray-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Performance</p>
              <p className="text-xs text-gray-500">Platform performance</p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportsPage;